// @owners { team: patients }
import { COLORS, SPACING } from '@alto/design-library-tokens';
import {
  Card,
  Column,
  ContentLoader,
  FlatCard,
  LgPadding,
  LoadingEmptyState,
  MdSpacing,
  SecondaryPage,
  SmSpacing,
} from '@alto/design-system';
import React from 'react';
import { type ImageSourcePropType } from 'react-native';

type Props = {
  /**
   * Surfaces that render a page heading instead of a nav bar title pass it here, so the loading
   * state matches the loaded page. When omitted the nav bar title is used, which is what native does.
   */
  readonly HeaderContent?: React.ReactNode;
  /**
   * The real illustration depends on the shipment status, which isn't known yet while loading, so
   * the caller decides which placeholder to show.
   */
  readonly illustrationSrc?: ImageSourcePropType;
  readonly illustrationAccessibilityLabel?: string;
};

export const OrderDetailsLoading = ({ HeaderContent, illustrationSrc, illustrationAccessibilityLabel }: Props) => {
  return (
    <SecondaryPage
      headerBackgroundColor={COLORS.BACKGROUND_COLORS.TERTIARY_LIGHTEST}
      HeaderContent={HeaderContent}
      NavBarCenterContent={HeaderContent === undefined ? 'Order details' : undefined}
    >
      <Card
        title="Loading order details..."
        overflow="visible"
        illustrationSrc={illustrationSrc}
        illustrationAccessibilityLabel={illustrationAccessibilityLabel}
      >
        <LgPadding topPadding={SPACING.STATIC.NONE}>
          <SmSpacing />
          <LoadingEmptyState />
        </LgPadding>
      </Card>
      <MdSpacing />
      <Card>
        <LgPadding>
          <Column center>
            <ContentLoader accessibilityLabel="loading medication details" />
          </Column>
        </LgPadding>
      </Card>
      <MdSpacing />
      <Card>
        <FlatCard>
          <LgPadding>
            <Column center>
              <ContentLoader accessibilityLabel="loading pricing details" />
            </Column>
          </LgPadding>
        </FlatCard>
      </Card>
    </SecondaryPage>
  );
};
